import { TDynamicFormConfig } from "@/features/Edit/dynamic-edit-form";
import {
  DEFAULT_ASSET_CLASS_OPTIONS_BUILDER,
  Option,
} from "@/features/Edit/common";
import { dateSchema, parseToDateValue } from "@/features/FormCard/utils";
import { z } from "zod";
import { C3_ASSET_CLASS_RELATIONSHIPS } from "./assetClassRules";
import { fieldTranslations } from "./translations";

const c3AssetClassSelect = Object.fromEntries(
  Object.entries(C3_ASSET_CLASS_RELATIONSHIPS).map(
    ([assetClass1, assetClass2Values]) => [
      assetClass1,
      Object.fromEntries(assetClass2Values.map((assetClass2) => [assetClass2, assetClass2])),
    ],
  ),
);

const maturity_date_conf = {
  label: fieldTranslations.maturity_date,
  validator: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional()
    .nullable()
    .describe("date"),
};

const coupon_rate_conf = {
  label: fieldTranslations.coupon_rate,
  validator: z.coerce.number().optional().nullable(),
};

const commitment_conf = {
  label: fieldTranslations.commitment,
  validator: z.coerce.number().optional().nullable(),
};

const capital_callable_conf = {
  label: fieldTranslations.capital_callable,
  validator: z.boolean().optional().nullable(),
};

// Detail fields shown for each asset_class_2 value; asset classes not listed
// here show no detail section.
const DETAIL_FIELDS_BY_ASSET_CLASS_2: Record<
  string,
  Record<string, { label: string; validator: z.ZodTypeAny }>
> = {
  private_debt: {
    maturity_date: maturity_date_conf,
    coupon_rate: coupon_rate_conf,
  },
  structured_product: {
    maturity_date: maturity_date_conf,
    coupon_rate: coupon_rate_conf,
  },
  private_equity_fund: {
    commitment: commitment_conf,
    capital_callable: capital_callable_conf,
  },
};

const c3AssetClassSection = Object.fromEntries(
  Object.values(C3_ASSET_CLASS_RELATIONSHIPS)
    .flat()
    .map((assetClass2) => [
      assetClass2,
      DETAIL_FIELDS_BY_ASSET_CLASS_2[assetClass2] ?? {},
    ]),
);

export const dynamicFormConfig: TDynamicFormConfig = {
  id: z.number(),
  name: z.string().nullable().optional(),
  isin: z.string().nullable().optional(),
  code: z.string().nullable().optional(),
  currency: z.string().nullable().optional(),
  strategy_id: z.coerce.number().nullable().optional(),
  asset_class_2: {
    key_config: {
      validator: {
        asset_class_1: z.string().nullable().optional(),
        asset_class_2: z.string().nullable().optional(),
      },
      primary_key: "asset_class_1",
      secondary_key: "asset_class_2",
    },
    select: c3AssetClassSelect,
    section: c3AssetClassSection,
  },
  nav_group: z.string().nullable().optional(),
  category: z.string().nullable().optional(),
  company_name: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
};

export const dynamicNewFormConfigWithoutId = Object.fromEntries(
  Object.entries(dynamicFormConfig).filter(([key]) => key !== "id"),
) as TDynamicFormConfig;

export const DEFAULT_C3_ASSET_CLASS_OPTIONS: Option[] =
  DEFAULT_ASSET_CLASS_OPTIONS_BUILDER(dynamicFormConfig);
